import { useState, useCallback, useRef } from "react";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { unlockAudio, getPersistentAudio } from "@/lib/audioHelper";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import type { VoiceAgentStatus } from "@/hooks/voice-agent/types";

export function useAudioPlayback(
  status: VoiceAgentStatus,
  setStatus: (s: VoiceAgentStatus) => void,
) {
  const [currentAudioElement, setCurrentAudioElement] = useState<HTMLAudioElement | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isSpeakingRef = useRef(false);

  // Stop speaking (declared before speak and playAudioMobileSafe to avoid TDZ)
  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    isSpeakingRef.current = false;
    setCurrentAudioElement(null);
    if (status === "speaking") {
      setStatus("idle");
    }
  }, [status, setStatus]);

  // Mobile-safe audio playback (declared before speak so speak can use it)
  const playAudioMobileSafe = useCallback(async (url: string): Promise<void> => {
    const persistentAudio = getPersistentAudio();
    if (persistentAudio) {
      return new Promise<void>((resolve, reject) => {
        audioRef.current = persistentAudio;
        isSpeakingRef.current = true;
        setCurrentAudioElement(persistentAudio);
        setStatus("speaking");
        persistentAudio.onended = () => {
          isSpeakingRef.current = false;
          setCurrentAudioElement(null);
          setStatus("idle");
          resolve();
        };
        persistentAudio.onerror = (e) => {
          logger.error("useVoiceAgent", "Audio playback error", e);
          isSpeakingRef.current = false;
          setCurrentAudioElement(null);
          setStatus("idle");
          reject(e);
        };
        persistentAudio.src = url;
        persistentAudio.load();
        const playPromise = persistentAudio.play();
        if (playPromise) {
          playPromise.catch((err) => {
            logger.warn("useVoiceAgent", "Audio play() rejected", err);
            isSpeakingRef.current = false;
            setCurrentAudioElement(null);
            setStatus("idle");
            reject(err);
          });
        }
      });
    }
    unlockAudio();
    return new Promise<void>((resolve, reject) => {
      const audio = new Audio();
      audioRef.current = audio;
      isSpeakingRef.current = true;
      setCurrentAudioElement(audio);
      setStatus("speaking");
      audio.onended = () => {
        isSpeakingRef.current = false;
        setCurrentAudioElement(null);
        setStatus("idle");
        resolve();
      };
      audio.onerror = (e) => {
        logger.error("useVoiceAgent", "Audio playback error", e);
        isSpeakingRef.current = false;
        setCurrentAudioElement(null);
        setStatus("idle");
        reject(e);
      };
      audio.src = url;
      audio.load();
      const playPromise = audio.play();
      if (playPromise) {
        playPromise.catch((err) => {
          logger.warn("useVoiceAgent", "Audio play() rejected (mobile autoplay policy)", err);
          isSpeakingRef.current = false;
          setCurrentAudioElement(null);
          setStatus("idle");
          reject(err);
        });
      }
    });
  }, [setStatus]);

  // Speak text via edge function text-to-speech (OpenAI TTS). Uses mobile-safe playback for iOS.
  const speak = useCallback(async (text: string) => {
    if (!text || isSpeakingRef.current) return;
    stopSpeaking();
    isSpeakingRef.current = true;
    setStatus("speaking");
    let objectUrl: string | null = null;
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/text-to-speech`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ text }),
        }
      );
      if (!response.ok) {
        throw new Error(`TTS error: ${response.status}`);
      }
      const audioBlob = await response.blob();
      objectUrl = URL.createObjectURL(audioBlob);
      // Use mobile-safe playback (persistent audio element on iOS) so playback works in all contexts
      await playAudioMobileSafe(objectUrl);
      logger.debug("useVoiceAgent", "Playing TTS audio");
    } catch (error) {
      logger.error("useVoiceAgent", "TTS error", error);
      isSpeakingRef.current = false;
      setStatus("idle");
      // Fallback: informative toast (not "désactivé" – audio failed or unavailable)
      toast.success("Réponse en texte uniquement", { description: "La lecture audio n'a pas pu être lancée. Le message s'affiche ci-dessous.",
        duration: 3000,
        variant: "default",
      });
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }, [playAudioMobileSafe, stopSpeaking, setStatus]);

  return {
    currentAudioElement,
    stopSpeaking,
    playAudioMobileSafe,
    speak,
  };
}
